import { FormControl, FormControlProps, Stack, TextField, Typography } from '@mui/material'
import React, {
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  ClipboardEvent,
  MouseEvent
} from 'react'
import { useTranslation } from 'react-i18next'

enum KeyboardKey {
  BACKSPACE = 'Backspace',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  ENTER = 'Enter'
}

const MAC_SEGMENTS = [0, 1, 2, 3, 4, 5] as const

interface RuuviTagMacAddressInputProps extends Omit<FormControlProps, 'onChange'> {
  /**
   * The handler for the change event.
   */
  onChange?: (value: string) => void
  /**
   * The externally provided MAC value without separators.
   */
  value?: string
}

export default function RuuviTagMacAddressInput({
  disabled,
  onChange,
  value: externalValue,
  ...props
}: RuuviTagMacAddressInputProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('installation')

  /**
   * The segments.
   */
  const segments = MAC_SEGMENTS

  /**
   * The input refs.
   */
  const refs = useRef<HTMLInputElement[]>([])

  /**
   * The input value as an array of segments.
   */
  const [segmentValues, setSegmentValues] = useState<string[]>(segments.map(() => ''))

  /**
   * The selection start for each input.
   */
  const [selectionStarts, setSelectionStarts] = useState<number[]>(segments.map(() => 0))

  /**
   * Handle the change event.
   */
  const handleChange = (index: number, inputValue: string) => {
    const newValue = [...segmentValues]
    newValue[index] = inputValue?.toUpperCase()
    setSegmentValues(newValue)

    // If the last character was entered, then focus the next input.
    // window.alert(value)
    if (inputValue.length === 2 && refs.current[index + 1]) {
      refs.current[index + 1].focus()
    }
  }

  function handleSelectionChange(index: number) {
    if (refs.current[index]) {
      const newSelectionStarts = [...selectionStarts]
      newSelectionStarts[index] = refs.current[index].selectionStart ?? 0
      setSelectionStarts(newSelectionStarts)
    }
  }

  /**
   * Handle the mouse down event.
   */
  const handleMouseDown = (
    index: number,
    _: MouseEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handleSelectionChange(index)
  }

  /**
   * Handle the key up event.
   */
  const handleKeyUp = (
    index: number,
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Get the cursor position of the input.
    const selectionStart = refs.current[index].selectionStart

    if (
      selectionStart === 0 &&
      selectionStarts[index] === 0 &&
      (event.key === KeyboardKey.ARROW_LEFT || event.key === KeyboardKey.BACKSPACE)
    ) {
      if (refs.current[index - 1]) {
        refs.current[index - 1].focus()
        refs.current[index - 1].setSelectionRange(2, 2)
      }
    } else if (
      selectionStart === 2 &&
      selectionStarts[index] === 2 &&
      (event.key === KeyboardKey.ARROW_RIGHT || event.key === KeyboardKey.ENTER)
    ) {
      if (refs.current[index + 1]) {
        refs.current[index + 1].focus()
        refs.current[index + 1].setSelectionRange(0, 0)
      }
    }

    handleSelectionChange(index)
  }

  /**
   * Handle the paste event.
   */
  const handlePaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault()

    const text = event.clipboardData.getData('text').replaceAll(':', '')

    const newValue = [...segmentValues]
    let segmentIndex = index

    // Combine characters into sets of two.
    const characterSets: string[] = text.match(/.{1,2}/g) ?? []

    characterSets.forEach((characters, i) => {
      segmentIndex = index + i
      const inputForIndexExists = segmentIndex < refs.current.length
      if (inputForIndexExists) {
        newValue[segmentIndex] = characters.toUpperCase()
      }
    })

    setSegmentValues(newValue)
  }

  /**
   * Build refs when the component mounts.
   */
  useEffect(() => {
    refs.current = refs.current.slice(0, segments.length)
  }, [])

  /**
   * Emit the change event when the value changes.
   */
  useEffect(() => {
    onChange?.(segmentValues.join('').toLowerCase())
  }, [segmentValues])

  /**
   * Sync the local state when external value changes.
   */
  useEffect(() => {
    if (externalValue === undefined) {
      return
    }

    const sanitized = externalValue.replace(/[^0-9A-Fa-f]/g, '').toUpperCase()

    if (sanitized.length === 0) {
      setSegmentValues(segments.map(() => ''))
      return
    }

    if (sanitized.length !== segments.length * 2) {
      return
    }

    const next = segments.map((segment) => sanitized.slice(segment * 2, segment * 2 + 2))
    setSegmentValues((prev) => {
      const isSame = next.every((segment, index) => segment === prev[index])
      return isSame ? prev : next
    })
  }, [externalValue])

  return (
    <FormControl sx={{ textAlign: 'center' }} fullWidth disabled={disabled} {...props}>
      {/*<Typography fontWeight={500} sx={{ mb: 2 }}>
        {t('enterMacAddress')}
      </Typography>*/}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        {segments.map((segment) => (
          <React.Fragment key={segment}>
            <TextField
              value={segmentValues[segment]}
              inputMode="text"
              inputProps={{
                ref: (el: HTMLInputElement) => (refs.current[segment] = el),
                inputMode: 'text',
                pattern: '[0-9A-Fa-f]{2}',
                maxLength: 2,
                sx: {
                  maxWidth: '24px',
                  flexGrow: 0,
                  fontSize: '16px !important',
                  textAlign: 'center !important',
                  p: 3
                },
                onKeyUp: (event) => {
                  handleKeyUp(segment, event)
                },
                onPaste: (event) => {
                  handlePaste(segment, event)
                },
                onMouseDown: (event) => {
                  handleMouseDown(segment, event)
                }
              }}
              disabled={disabled}
              onChange={(event) => {
                handleChange(segment, event.target.value)
              }}
            />
            {segment !== segments.length - 1 && (
              <Typography color="text.disabled" sx={{ fontSize: '16px', mt: 1 }}>
                :
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </FormControl>
  )
}
